import {
    type CSSProperties,
    type FC,
    Fragment,
    type ReactNode,
    useContext,
} from "react";
import { isRecord } from "../../../../../types/internal/utility";
import { createStyleFromPadding } from "../../PaddingInput";
import { createBorderStyles } from "../BorderSettingsDialog";
import { DragDropComponentEmptyContext } from "../DragDropComponent";
import type { ComponentOptions, DragDropTableColumnType } from "../DragDropTypes";

type CellStyleCallback = (
    column: string,
    columns: Array<DragDropTableColumnType>
) => CSSProperties;

interface TableColumnDefinition {
    style?: CSSProperties | CellStyleCallback;
    format?: (value: unknown, row: Record<string, unknown>) => ReactNode;
}

export type TableColumnMap = Record<string, TableColumnDefinition>;

type RowStyleCallback = (
    row: Record<string, unknown>,
    columns: Array<DragDropTableColumnType>
) => CSSProperties;

interface Props extends ComponentOptions {
    noHeader?: boolean;
    renderExtra?: (
        row: Record<string, unknown>,
        columns: Array<DragDropTableColumnType>,
        style: CSSProperties
    ) => ReactNode;
    rowStyle?: CSSProperties | RowStyleCallback;
    columns: TableColumnMap;
    value: Array<DragDropTableColumnType>;
    data: Array<unknown>;
}

export const TableComponent: FC<Props> = ({
    noHeader,
    renderExtra,
    rowStyle,
    columns,
    style,
    value,
    data,
}) => {
    const empty = useContext(DragDropComponentEmptyContext);

    if(!Array.isArray(data)) {
        return null;
    }

    const validColumns: Array<DragDropTableColumnType> = [];

    for(let i = 0, l = value.length; i < l; i++) {
        const column = value[i];

        if(!column.enabled) {
            continue;
        }

        // Check to see if the column should exist
        if(!(column.column in columns)) {
            continue;
        }

        validColumns.push(column);
    }

    if(!validColumns.length) {
        return empty;
    }

    const { fontSize, border, padding, ...restStyle } = style;

    const wrapperStyle: CSSProperties = {
        width: "100%",
        ...(typeof border !== "undefined" ? createBorderStyles(border) : {}),
        ...(typeof padding !== "undefined" ? createStyleFromPadding(padding) : {}),
    };

    const tableStyle: CSSProperties = {
        width   : "100%",
        ...restStyle,
        fontSize: typeof fontSize === "undefined" ? undefined : `${fontSize}px`,
    };

    const dStyle: CSSProperties = {
        border       : "none",
        paddingTop   : 0,
        paddingBottom: 0,
        textAlign    : "right",
    };

    return (
        <div style={wrapperStyle}>
            <table style={tableStyle}>
                {!noHeader && (
                    <thead>
                        <tr>
                            {validColumns.map((column, index) => {
                                let hStyle: CSSProperties;
                                const columnStyle = columns[column.column].style;

                                if(columnStyle) {
                                    hStyle = {
                                        ...dStyle,
                                        ...(typeof columnStyle === "function" ?
                                            columnStyle(column.column, validColumns) :
                                            columnStyle
                                        ),
                                    };
                                } else {
                                    hStyle = {
                                        ...dStyle,
                                    };
                                }

                                if(index === 0) {
                                    hStyle.paddingLeft = "0";

                                    if(typeof hStyle.textAlign === "undefined") {
                                        hStyle.textAlign = "left";
                                    }
                                }

                                if(index === validColumns.length - 1) {
                                    hStyle.paddingRight = "0";
                                }

                                return (
                                    <th
                                        key={column.column}
                                        className={column.column}
                                        style={hStyle}
                                    >
                                        {column.name}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                )}
                <tbody>
                    {data.map((row, rowIndex) => {
                        if(!isRecord(row)) {
                            return null;
                        }

                        const rStyle = {
                            ...dStyle,
                            ...(typeof rowStyle === "function" ? rowStyle(row, validColumns) : {}),
                        };

                        return (
                            <Fragment key={rowIndex}>
                                <tr>
                                    {validColumns.map((column, index) => {
                                        const { style: columnStyle, format } = columns[column.column];

                                        let cStyle: CSSProperties;

                                        if(columnStyle) {
                                            cStyle = {
                                                ...rStyle,
                                                ...(typeof columnStyle === "function" ?
                                                    columnStyle(column.column, validColumns) :
                                                    columnStyle
                                                ),
                                            };
                                        } else {
                                            cStyle = {
                                                ...rStyle,
                                            };
                                        }

                                        if(index === 0) {
                                            cStyle.paddingLeft = "0";

                                            if(typeof cStyle.textAlign === "undefined") {
                                                cStyle.textAlign = "left";
                                            }
                                        }

                                        if(index === validColumns.length - 1) {
                                            cStyle.paddingRight = "0";
                                        }

                                        const datum = row[column.column];

                                        return (
                                            <td
                                                key={column.column}
                                                className={column.column}
                                                style={cStyle}
                                            >
                                                {(typeof format === "function" ?
                                                    format(datum, row) :
                                                    datum as string
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {typeof renderExtra === "function" && (
                                    renderExtra(row, validColumns, dStyle)
                                )}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
